import React, { FormEvent } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import {
  // Selectors
  orderedSearchResults,
  offersQuerySelector,
  offersSearchPendingSelector,
  activeLodingIndexSelector,
  totalGuestCountSelector,
  showLodgingControlsSelector,
  nameSearchResultsSelector,
  nameSearchResultsRawSelector,
  nameSearchPendingSelector,
  showNameSearchResultsSelector,
  totalStayNightsSelector,
  dateRangeSelector,
  dateRangeDisplayStringSelector,
  selectedDatesSelector,
  datePickerCurrentDateSelector,
  dateSelectionInProgressSelector,
  showDatePickerSelector,
  isRepeatGuestSelector,
  canSearchSelector,

  // Actions
  initializeQueryAction,
  clearExtendedQueryAction,
  offersSearchRequestAction,
  toggleHighlightsAction,
  destinationChangeAction,
  incrementRoomAction,
  incrementAdultAction,
  incrementChildAction,
  setAgeAction,
  setActiveLodgingIndexAction,
  incrementActiveLodgingIndexAction,
  toggleLodgingControlsAction,
  setLodgingControlsVisibilityAction,
  setNamesSearchResultsVisibilityAction,
  toggleRepeatGuestAction,
  dateRangeChangeAction,
  clearSearchResultsAction,
  clientCountryCodeChangeAction,
  hotelsSearchResultsSelector,
} from 'store/modules/fastSearch';

import { isInternalUser, isTA } from 'store/modules/auth';

import { clearBookingBuilderAction } from 'store/modules/bookingBuilder';
import { TCountryCode } from 'interfaces';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';

// Connect to the store
// ---------------------------------------------------------------------------------------------------------------
const mapStateToProps = createStructuredSelector<any, any>({
  searchPending: offersSearchPendingSelector,
  searchResults: orderedSearchResults,
  searchQuery: offersQuerySelector,
  activeLodingIndex: activeLodingIndexSelector,
  totalGuestCount: totalGuestCountSelector,
  showLodgingControls: showLodgingControlsSelector,
  nameSearchResults: nameSearchResultsSelector,
  nameSearchResultsRaw: nameSearchResultsRawSelector,
  nameSearchPending: nameSearchPendingSelector,
  showNameSearchResults: showNameSearchResultsSelector,
  totalStayNights: totalStayNightsSelector,
  dateRange: dateRangeSelector,
  dateRangeDisplayString: dateRangeDisplayStringSelector,
  selectedDates: selectedDatesSelector,
  currentDate: datePickerCurrentDateSelector,
  dateSelectionInProgress: dateSelectionInProgressSelector,
  showDatePicker: showDatePickerSelector,
  isRepeatGuest: isRepeatGuestSelector,
  canSearch: canSearchSelector,
  isSr: isInternalUser,
  isTa: isTA,
  destinationList: hotelsSearchResultsSelector,
});

const actionCreators = {
  getOffers: offersSearchRequestAction,
  destinationChange: destinationChangeAction,
  incrementRoom: incrementRoomAction,
  incrementAdult: incrementAdultAction,
  incrementChild: incrementChildAction,
  setAge: setAgeAction,
  setActiveLodgingIndex: setActiveLodgingIndexAction,
  toggleHighlights: toggleHighlightsAction,
  incrementActiveLodgingIndex: incrementActiveLodgingIndexAction,
  toggleLodgingControls: toggleLodgingControlsAction,
  setLodgingControlsVisibility: setLodgingControlsVisibilityAction,
  setNamesSearchResultsVisibility: setNamesSearchResultsVisibilityAction,
  initializeQuery: initializeQueryAction,
  toggleRepeatGuest: toggleRepeatGuestAction,
  clearBookingBuilderAction,
  clearExtendedQuery: clearExtendedQueryAction,
  dateRangeChangeAction,
  clearSearchResultsAction,
  guestCountryChangeAction: clientCountryCodeChangeAction,
};

export interface IWithBasicSearchHandlers {
  handleDestinationChange: (e: FormEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  handleNavigateToSearch: () => void;
  handleToggleLodgingControls: () => void;
  handleSetLogdingControlsVisibility: (visible: boolean) => (e: any) => void;
  handleShowNameSearchDropDown: (visible: boolean) => (e: any) => void;
  toggleRepeatGuest: () => void;
  handleDateChange: (dates: string[]) => void;
  handleGuestCountryChange: (e: FormEvent<HTMLInputElement>) => void;
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(actionCreators, dispatch);
type IStateToProps = ReturnType<typeof mapStateToProps>;
type IDispatchToProps = typeof actionCreators;
export interface IWithBasicSearchProps extends IStateToProps, IDispatchToProps, RouteComponentProps {
  basicSearchHandlers: IWithBasicSearchHandlers;
}

// ----------------------------------------------------------
// For testing purposes, create the class with a function so
// we can test the unconnected version
// ----------------------------------------------------------
export const makeWithBasicSearch = (WrappedComponent: any) =>
  class WithBasicSearch extends React.Component<IWithBasicSearchProps> {
    static displayName = `WithBasicSearch(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
    componentDidMount() {
      if (this.props.searchQuery.name === '') {
        this.props.destinationChange('');
      }
    }
    handleDestinationChange = (e: FormEvent<HTMLInputElement>) => {
      this.props.destinationChange(e.currentTarget.value);
    };

    handleSubmit = () => {
      this.props.getOffers(this.props.searchQuery);
      clearBookingBuilderAction();
    };

    handleNavigateToSearch = () => {
      const dynamicParameters = ParameterService.getParameters();
      if (dynamicParameters.WOOPRA_DOMAIN) {
        // @ts-ignore
        window.woopra &&
          // @ts-ignore
          window.woopra.track('search', {
            search: this.props.searchQuery.name,
            startDate: this.props.searchQuery.startDate,
            endDate: this.props.searchQuery.endDate,
            isTA: this.props.isTa,
          });
      }
      this.props.clearExtendedQuery();
      this.props.clearSearchResultsAction();
      this.props.onSearch && this.props.onSearch(this.props.searchQuery, this.props.destinationList);
    };

    handleToggleLodgingControls = () => {
      this.props.toggleLodgingControls();
    };

    handleSetLogdingControlsVisibility = (visible: boolean) => () => {
      this.props.setLodgingControlsVisibility(visible);
    };

    handleShowNameSearchDropDown = (visible: boolean) => () => {
      this.props.setNamesSearchResultsVisibility(visible);
    };

    handleGuestCountryChange = (e: FormEvent<HTMLInputElement>) => () => {
      this.props.guestCountryChangeAction(e.currentTarget.value as TCountryCode);
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          basicSearchHandlers={{
            handleDestinationChange: this.handleDestinationChange,
            handleSubmit: this.handleSubmit,
            handleNavigateToSearch: this.handleNavigateToSearch,
            handleToggleLodgingControls: this.handleToggleLodgingControls,
            handleSetLogdingControlsVisibility: this.handleSetLogdingControlsVisibility,
            handleShowNameSearchDropDown: this.handleShowNameSearchDropDown,
            handleDateChange: this.props.dateRangeChangeAction,
            handleGuestCountryChange: this.handleGuestCountryChange,
          }}
        />
      );
    }
  };

export const WithBasicSearch = () => WrappedComponent => {
  const instance = makeWithBasicSearch(WrappedComponent);
  const withConnect = connect(mapStateToProps, mapDispatchToProps);

  const routed = withRouter(instance);
  const composed = withConnect(routed);

  return hoistNonReactStatics(composed, WrappedComponent);
};

export default WithBasicSearch;
